import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Power, PowerOff, ShieldAlert, Loader2, Info, Wrench, AlertOctagon, Save, ShieldOff } from "lucide-react";
import DiscordResetPanel from "@/components/DiscordResetPanel";
import HostNoticeSettings from "@/components/HostNoticeSettings";
import ShowAlertsSettings from "@/components/ShowAlertsSettings";

/**
 * Admin-only kill switches for Discord features. Two independent toggles
 * (OAuth login + bot workflow) plus a "master" convenience row that flips
 * both at once. When either is turned OFF, non-admin Discord sessions are
 * invalidated on the backend, but admins stay signed in.
 */
export default function SiteSettings() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(null); // { field, next } | { master: bool }

  const load = async () => {
    try {
      const { data } = await api.get("/admin/site-settings");
      setSettings(data);
      setError("");
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || "Failed to load site settings");
    }
  };

  useEffect(() => { load(); }, []);

  const apply = async (patch) => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/site-settings", patch);
      setSettings((prev) => ({ ...prev, ...data }));
      toast.success("Settings updated");
      // Let the MaintenanceGate refresh its banner immediately on maintenance change.
      if (Object.prototype.hasOwnProperty.call(patch, "maintenance_mode")) {
        window.dispatchEvent(new Event("maintenance-settings-changed"));
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Update failed");
    } finally {
      setSaving(false);
      setConfirmOpen(null);
    }
  };

  const requestToggle = (field, current) => {
    const next = !current;
    // Turning OFF requires confirmation because it force-logs-out fans.
    if (!next) {
      setConfirmOpen({ field, next });
    } else {
      apply({ [field]: next });
    }
  };

  const requestMasterToggle = (turnOn) => {
    if (!turnOn) {
      setConfirmOpen({ master: true });
    } else {
      apply({ discord_login_enabled: true, discord_bot_enabled: true });
    }
  };

  if (!settings && !error) {
    return (
      <div className="p-8 text-[var(--ff-text-dim)] flex items-center gap-3" data-testid="site-settings-loading">
        <Loader2 className="animate-spin" size={16} /> Loading site settings…
      </div>
    );
  }

  if (error) {
    return <div className="p-8 text-[var(--ff-red)] text-sm" data-testid="site-settings-error">{error}</div>;
  }

  const bothOn = settings.discord_login_enabled && settings.discord_bot_enabled;
  const bothOff = !settings.discord_login_enabled && !settings.discord_bot_enabled;

  return (
    <div className="space-y-8" data-testid="site-settings-page">
      <div>
        <div className="label-eyebrow text-[var(--ff-red)] mb-2">▮ ADMIN / KILL SWITCH</div>
        <h2 className="font-display text-3xl mb-2">Discord Feature Toggles</h2>
        <p className="text-[var(--ff-text-dim)] text-sm max-w-2xl">
          Instantly disable Discord features across the site. Turning something OFF logs out
          all fans and hosts using Discord auth. You (admin) stay signed in so you can flip
          it back on. Last change: {settings.updated_at ? new Date(settings.updated_at).toLocaleString() : "never"}
          {settings.updated_by ? ` by ${settings.updated_by}` : ""}.
        </p>
      </div>

      {/* Master switch */}
      <div className="bg-[var(--ff-surface)] border border-[var(--ff-border)] p-6 flex items-center justify-between gap-6" data-testid="master-switch-row">
        <div className="flex items-start gap-4">
          {bothOff ? <PowerOff size={26} className="text-[var(--ff-red)] mt-1" /> : <Power size={26} className="text-emerald-400 mt-1" />}
          <div>
            <div className="font-mono-ui text-sm uppercase tracking-[0.15em] mb-1">
              Master Discord Switch
            </div>
            <div className="text-xs text-[var(--ff-text-dim)] max-w-xl">
              Convenience toggle: flips both switches at once. Use in an emergency (e.g. Discord
              incident, moderation event) when you need to isolate the site from Discord entirely.
            </div>
          </div>
        </div>
        <button
          onClick={() => requestMasterToggle(!bothOn)}
          disabled={saving}
          data-testid="master-switch-toggle"
          className={`px-5 py-2.5 font-mono-ui text-xs uppercase tracking-[0.18em] transition-colors disabled:opacity-50 ${
            bothOn
              ? "bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30"
              : "bg-[var(--ff-red)]/20 text-[var(--ff-red)] hover:bg-[var(--ff-red)]/30"
          }`}
        >
          {bothOn ? "All ON — Turn Off All" : bothOff ? "All OFF — Turn On All" : "Mixed — Turn On All"}
        </button>
      </div>

      {/* Individual toggles */}
      <div className="grid md:grid-cols-2 gap-px bg-[var(--ff-border)]">
        <ToggleCard
          testId="toggle-discord-login"
          label="Discord OAuth Login"
          enabled={settings.discord_login_enabled}
          configured={settings.discord_login_configured}
          onToggle={() => requestToggle("discord_login_enabled", settings.discord_login_enabled)}
          saving={saving}
          description={
            <>
              Controls the “Continue with Discord” button on <span className="font-mono-ui">/login</span>.
              When OFF: new Discord sign-ins are blocked, fans/hosts are logged out, existing admin
              sessions untouched.
            </>
          }
        />
        <ToggleCard
          testId="toggle-discord-bot"
          label="Discord Bot Workflow"
          enabled={settings.discord_bot_enabled}
          configured={settings.discord_bot_configured}
          onToggle={() => requestToggle("discord_bot_enabled", settings.discord_bot_enabled)}
          saving={saving}
          description={
            <>
              Controls the bot side: show-reminder DMs, appeal-outcome DMs, auto-ban enforcement,
              and <span className="font-mono-ui">/blacklist</span> slash commands. When OFF: bot
              stays connected but ignores workflow triggers.
            </>
          }
        />
      </div>

      <div className="flex items-start gap-3 text-xs text-[var(--ff-text-dim)] bg-[var(--ff-surface)]/50 border border-[var(--ff-border)] p-4" data-testid="site-settings-note">
        <Info size={16} className="text-[var(--ff-red)] mt-0.5 shrink-0" />
        <div>
          Kill switches are runtime-only — no <span className="font-mono-ui">.env</span> changes required.
          Re-enable at any time and Discord features resume immediately. Fans will need to sign in
          again the next time login is turned back on.
        </div>
      </div>

      <PaymentProviderPicker
        value={settings.payment_provider || "stripe"}
        onChange={(next) => apply({ payment_provider: next })}
        saving={saving}
      />

      <PaymentsKillSwitch
        enabled={settings.payments_enabled !== false}
        disabledAt={settings.payments_disabled_at}
        saving={saving}
        onToggle={(next, onDone) => {
          if (next === false) {
            setConfirmOpen({ payments: true, onDone });
          } else {
            apply({ payments_enabled: true });
            onDone && onDone();
          }
        }}
      />

      <MaintenanceModeSection
        enabled={settings.maintenance_mode === true}
        message={settings.maintenance_message || ""}
        since={settings.maintenance_enabled_at}
        saving={saving}
        onToggle={(next) => (next ? setConfirmOpen({ maintenance: true }) : apply({ maintenance_mode: false }))}
        onSaveMessage={(msg) => apply({ maintenance_message: msg })}
      />

      <CodeRedProtocolSection
        enabled={settings.bridge_enabled !== false}
        configured={settings.bridge_configured}
        since={settings.bridge_disabled_at}
        clUrl={settings.community_lounge_url}
        saving={saving}
        onToggle={(next) => (next ? apply({ bridge_enabled: true }) : setConfirmOpen({ codered: true }))}
      />

      <CodeCleanSlateSection
        enabled={settings.autoban_enabled !== false}
        since={settings.autoban_disabled_at}
        saving={saving}
        onToggle={(next) => apply({ autoban_enabled: next })}
      />

      <HostNoticeSettings />
      <ShowAlertsSettings />

      <DiscordResetPanel onDone={load} />

      {confirmOpen && (
        <ConfirmDialog
          onCancel={() => setConfirmOpen(null)}
          onConfirm={() => {
            if (confirmOpen.master) {
              apply({ discord_login_enabled: false, discord_bot_enabled: false });
            } else if (confirmOpen.payments) {
              apply({ payments_enabled: false }).then(() => confirmOpen.onDone && confirmOpen.onDone());
            } else if (confirmOpen.maintenance) {
              apply({ maintenance_mode: true });
            } else if (confirmOpen.codered) {
              apply({ bridge_enabled: false });
            } else {
              apply({ [confirmOpen.field]: false });
            }
          }}
          saving={saving}
          master={!!confirmOpen.master}
          payments={!!confirmOpen.payments}
          maintenance={!!confirmOpen.maintenance}
          codered={!!confirmOpen.codered}
          field={confirmOpen.field}
        />
      )}
    </div>
  );
}

function ToggleCard({ testId, label, enabled, configured, description, onToggle, saving }) {
  const stateLabel = !configured
    ? "Not Configured"
    : enabled
    ? "Enabled"
    : "Disabled by Admin";
  const stateColor = !configured
    ? "text-yellow-400"
    : enabled
    ? "text-emerald-400"
    : "text-[var(--ff-red)]";
  return (
    <div className="bg-[var(--ff-surface)] p-6 flex flex-col justify-between gap-4" data-testid={testId}>
      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="font-mono-ui text-sm uppercase tracking-[0.15em]">{label}</div>
          <span className={`label-eyebrow ${stateColor}`} data-testid={`${testId}-state`}>{stateLabel}</span>
        </div>
        <p className="text-xs text-[var(--ff-text-dim)] leading-relaxed">{description}</p>
        {!configured && (
          <p className="text-xs text-yellow-400/80 mt-2 flex items-center gap-1.5">
            <ShieldAlert size={12} /> Missing env credentials — feature can’t be turned on.
          </p>
        )}
      </div>
      <button
        onClick={onToggle}
        disabled={saving || !configured}
        data-testid={`${testId}-btn`}
        className={`self-start px-4 py-2 font-mono-ui text-xs uppercase tracking-[0.18em] transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
          enabled
            ? "bg-[var(--ff-red)]/20 text-[var(--ff-red)] hover:bg-[var(--ff-red)]/30"
            : "bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30"
        }`}
      >
        {enabled ? "Turn Off" : "Turn On"}
      </button>
    </div>
  );
}

function ConfirmDialog({ onCancel, onConfirm, saving, master, payments, maintenance, codered, field }) {
  const title = payments
    ? "Turn off payments?"
    : maintenance
    ? "Take the whole site down?"
    : codered
    ? "Engage Code Red Protocol?"
    : master
    ? "Turn off all Discord features?"
    : field === "discord_login_enabled"
    ? "Turn off Discord login?"
    : "Turn off Discord bot workflow?";
  const impact = payments
    ? "Every active Stripe + PayPal subscription will be canceled immediately. All Stripe Prices and PayPal Plans will be deactivated. New checkouts are blocked site-wide. This does NOT delete data — click 'Purge Payment Data Now' after this if you also want to wipe memberships, transactions, earnings, and tiers."
    : maintenance
    ? "Every visitor except founders and admins will immediately see the Under Maintenance page, and their API calls will be blocked. You (admin) keep full access and can turn it back off any time. /login stays reachable so staff can still sign in."
    : codered
    ? "The Community Lounge ⇄ Freshy Fresh bridge goes fully dormant: SSO login/verify, role lookups, and ban/unban sync all return 503, outbound ban mirroring stops, and the 'Continue with Community Lounge' button hides. The Bridge Audit log stays viewable. Lift it any time to resume."
    : master
    ? "All fans and hosts will be logged out. New Discord sign-ins will be blocked, no reminder DMs will fire, and slash commands will stop responding until re-enabled."
    : field === "discord_login_enabled"
    ? "All fans and hosts currently signed in via Discord will be logged out. New Discord sign-ins will show 'Discord Unavailable' on the login page."
    : "Show-reminder DMs, appeal-outcome DMs, /blacklist slash commands, and auto-ban enforcement will pause until re-enabled. Existing sessions stay active.";
  const confirmLabel = maintenance
    ? "Yes, take site down"
    : codered
    ? "Engage Code Red"
    : "Yes, turn off";
  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-6" data-testid="confirm-toggle-off-dialog">
      <div className="w-full max-w-md bg-[var(--ff-surface)] border border-[var(--ff-red)]/40 p-6">
        <div className="flex items-center gap-3 mb-3">
          <ShieldAlert className="text-[var(--ff-red)]" size={20} />
          <h3 className="font-display text-xl">{title}</h3>
        </div>
        <p className="text-sm text-[var(--ff-text-dim)] mb-6 leading-relaxed">{impact}</p>
        <div className="flex justify-end gap-3">
          <button
            onClick={onCancel}
            disabled={saving}
            data-testid="confirm-toggle-cancel"
            className="px-4 py-2 font-mono-ui text-xs uppercase tracking-[0.18em] text-[var(--ff-text-dim)] hover:text-white transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={saving}
            data-testid="confirm-toggle-confirm"
            className="px-4 py-2 font-mono-ui text-xs uppercase tracking-[0.18em] bg-[var(--ff-red)] text-white hover:brightness-110 transition-all disabled:opacity-50"
          >
            {saving ? "Saving…" : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}


function PaymentProviderPicker({ value, onChange, saving }) {
  const [cfg, setCfg] = React.useState(null);
  const [creatingHook, setCreatingHook] = React.useState(false);
  React.useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/config/paypal");
        setCfg(data);
      } catch {
        setCfg({ configured: false, webhook_ready: false, env: "sandbox" });
      }
    })();
  }, []);

  const options = [
    { id: "stripe", label: "Stripe Only", desc: "Card checkout via Stripe. PayPal button hidden." },
    { id: "paypal", label: "PayPal Only", desc: "PayPal Subscriptions. Stripe button hidden." },
    { id: "both",   label: "Both Side-by-Side", desc: "Show Stripe + PayPal buttons on every tier." },
  ];

  const createWebhook = async () => {
    setCreatingHook(true);
    try {
      const webhookUrl = `${window.location.origin}/api/paypal/webhook`;
      const { data } = await api.post("/admin/paypal/webhooks", { webhook_url: webhookUrl });
      toast.success(`Webhook created · id ${data.webhook?.id}. Add PAYPAL_${cfg.env.toUpperCase()}_WEBHOOK_ID to backend .env and restart.`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Webhook create failed");
    } finally {
      setCreatingHook(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="payment-provider-picker">
      <div>
        <div className="label-eyebrow text-[var(--ff-red)] mb-2">▮ ADMIN / PAYMENTS</div>
        <h2 className="font-display text-3xl mb-2">Payment Provider</h2>
        <p className="text-[var(--ff-text-dim)] text-sm max-w-2xl">
          Pick who processes membership subscriptions. Changes take effect immediately for every show&apos;s tier picker.
        </p>
      </div>

      <div className="grid md:grid-cols-3 gap-px bg-[var(--ff-border)]">
        {options.map((o) => {
          const disabled = saving || (o.id !== "stripe" && !cfg?.configured);
          const selected = value === o.id;
          return (
            <button
              key={o.id}
              onClick={() => !selected && onChange(o.id)}
              disabled={disabled}
              data-testid={`payment-provider-${o.id}`}
              className={`text-left p-5 transition-colors ${selected ? "bg-[var(--ff-red)] text-white" : "bg-[var(--ff-surface)] hover:bg-[var(--ff-surface-hover)] text-white"} disabled:opacity-40`}
            >
              <div className="font-display text-lg mb-1">{o.label}</div>
              <div className="text-[0.7rem] font-mono-ui uppercase tracking-[0.15em] opacity-80">{o.desc}</div>
              {selected && (
                <div className="mt-3 text-[0.55rem] font-mono-ui uppercase tracking-[0.15em] opacity-90">✓ Active</div>
              )}
            </button>
          );
        })}
      </div>

      <div className="bg-[var(--ff-surface)]/50 border border-[var(--ff-border)] p-4 text-xs text-[var(--ff-text-dim)] leading-relaxed" data-testid="paypal-status-card">
        <div className="flex items-center gap-2 mb-2">
          <span className={`inline-block w-2 h-2 rounded-full ${cfg?.configured ? "bg-emerald-400" : "bg-yellow-400"}`} />
          <span className="text-white font-mono-ui uppercase tracking-[0.15em] text-[0.65rem]">
            PayPal · {cfg?.env || "sandbox"} · {cfg?.configured ? "Configured" : "Not configured"} · Webhook {cfg?.webhook_ready ? "ready" : "missing"}
          </span>
        </div>
        {cfg?.configured && !cfg?.webhook_ready && (
          <button
            onClick={createWebhook}
            disabled={creatingHook}
            className="btn-ghost text-xs mt-1"
            data-testid="paypal-create-webhook"
          >
            {creatingHook ? "Creating…" : "Auto-create PayPal webhook"}
          </button>
        )}
        {!cfg?.configured && (
          <div>Set <span className="font-mono-ui">PAYPAL_SANDBOX_CLIENT_ID/SECRET</span> in backend .env and restart backend.</div>
        )}
      </div>
    </div>
  );
}


function PaymentsKillSwitch({ enabled, disabledAt, saving, onToggle }) {
  const [status, setStatus] = React.useState(null);
  const [purging, setPurging] = React.useState(false);
  const loadStatus = async () => {
    try {
      const { data } = await api.get("/admin/payments/status");
      setStatus(data);
    } catch { setStatus(null); }
  };
  React.useEffect(() => { loadStatus(); }, [enabled]);

  const purge = async () => {
    if (!window.confirm(
      "PURGE ALL PAYMENT DATA?\n\n" +
      "This permanently deletes every membership, payment transaction, host earning, and membership tier from the database. " +
      "Provider assets on Stripe / PayPal are already deactivated. This cannot be undone.\n\n" +
      "Type YES in the next prompt to confirm."
    )) return;
    const confirmText = window.prompt("Type YES to confirm the purge:");
    if ((confirmText || "").trim() !== "YES") { toast.info("Purge cancelled."); return; }
    setPurging(true);
    try {
      const { data } = await api.post("/admin/payments/purge");
      const c = data.purged || {};
      toast.success(`Purged · ${c.memberships} memberships · ${c.payment_transactions} txns · ${c.host_earnings} earnings · ${c.membership_tiers} tiers`);
      loadStatus();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Purge failed");
    } finally { setPurging(false); }
  };

  return (
    <div className="space-y-4" data-testid="payments-kill-switch">
      <div>
        <div className="label-eyebrow text-[var(--ff-red)] mb-2">▮ ADMIN / PAYMENTS MASTER SWITCH</div>
        <h2 className="font-display text-3xl mb-2">Payments Enabled</h2>
        <p className="text-[var(--ff-text-dim)] text-sm max-w-2xl">
          Turning OFF cancels every live Stripe + PayPal subscription immediately, deactivates every price + plan,
          and blocks new checkouts. Data is NOT deleted by the toggle — click Purge below if you also want to wipe
          memberships, transactions, earnings, and tiers.
          {disabledAt && !enabled && <span className="block mt-2 text-yellow-400">Disabled at {new Date(disabledAt).toLocaleString()}.</span>}
        </p>
      </div>

      <div className="bg-[var(--ff-surface)] p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4" data-testid="payments-master-toggle-card">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className={`inline-block w-2 h-2 rounded-full ${enabled ? "bg-emerald-400" : "bg-[var(--ff-red)]"}`} />
            <span className="font-mono-ui text-sm uppercase tracking-[0.15em]">Payments</span>
            <span className={`label-eyebrow ${enabled ? "text-emerald-400" : "text-[var(--ff-red)]"}`} data-testid="payments-master-state">
              {enabled ? "Enabled" : "Disabled by Admin"}
            </span>
          </div>
          {status && (
            <div className="text-[0.65rem] font-mono-ui uppercase tracking-[0.15em] text-[var(--ff-text-dim)]">
              {status.counts.active_memberships} active subs · {status.counts.memberships} lifetime · {status.counts.membership_tiers} tiers · {status.counts.host_earnings} earnings rows · {status.counts.payment_transactions} txns
            </div>
          )}
        </div>
        <button
          onClick={() => onToggle(!enabled, loadStatus)}
          disabled={saving}
          data-testid="payments-master-toggle-btn"
          className={`px-4 py-2 font-mono-ui text-xs uppercase tracking-[0.18em] transition-all disabled:opacity-50 ${
            enabled ? "bg-[var(--ff-red)]/20 text-[var(--ff-red)] hover:bg-[var(--ff-red)]/30" : "bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30"
          }`}
        >
          {saving ? "Saving…" : enabled ? "Turn Off Payments" : "Turn Payments Back On"}
        </button>
      </div>

      {!enabled && (
        <div className="border border-[var(--ff-red)]/40 bg-[var(--ff-red)]/5 p-6" data-testid="payments-purge-panel">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div>
              <div className="font-display text-lg text-[var(--ff-red)]">Purge Payment Data Now</div>
              <div className="text-[0.65rem] font-mono-ui uppercase tracking-[0.15em] text-[var(--ff-text-dim)] mt-1">
                Permanently delete every membership, transaction, host earning row, and tier from the database. Cannot be undone.
              </div>
            </div>
            <button
              onClick={purge}
              disabled={purging}
              data-testid="payments-purge-btn"
              className="px-4 py-2 font-mono-ui text-xs uppercase tracking-[0.18em] bg-[var(--ff-red)] text-white hover:brightness-110 transition-all disabled:opacity-50"
            >
              {purging ? "Purging…" : "Purge Now →"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


function MaintenanceModeSection({ enabled, message, since, saving, onToggle, onSaveMessage }) {
  const [draft, setDraft] = React.useState(message || "");
  React.useEffect(() => { setDraft(message || ""); }, [message]);
  const dirty = (draft || "") !== (message || "");

  return (
    <div className="space-y-4" data-testid="maintenance-mode-section">
      <div>
        <div className="label-eyebrow text-[var(--ff-red)] mb-2">▮ ADMIN / SITE STATUS</div>
        <h2 className="font-display text-3xl mb-2">Maintenance Mode</h2>
        <p className="text-[var(--ff-text-dim)] text-sm max-w-2xl">
          Take the entire public site offline behind an Under Maintenance page. Founders &amp; admins keep full access
          (and can turn it back off), and <span className="font-mono-ui">/login</span> stays reachable so staff can sign in.
          {since && enabled && <span className="block mt-2 text-yellow-400">On since {new Date(since).toLocaleString()}.</span>}
        </p>
      </div>

      <div className="bg-[var(--ff-surface)] p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4" data-testid="maintenance-toggle-card">
        <div className="flex items-start gap-4">
          <Wrench size={26} className={enabled ? "text-[var(--ff-red)] mt-1" : "text-emerald-400 mt-1"} />
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="font-mono-ui text-sm uppercase tracking-[0.15em]">Site Status</span>
              <span className={`label-eyebrow ${enabled ? "text-[var(--ff-red)]" : "text-emerald-400"}`} data-testid="maintenance-state">
                {enabled ? "Under Maintenance" : "Live"}
              </span>
            </div>
            <div className="text-xs text-[var(--ff-text-dim)] max-w-xl">
              When ON, every fan/host/guest sees the maintenance page and their API calls are blocked (503).
            </div>
          </div>
        </div>
        <button
          onClick={() => onToggle(!enabled)}
          disabled={saving}
          data-testid="maintenance-toggle-btn"
          className={`px-4 py-2 font-mono-ui text-xs uppercase tracking-[0.18em] transition-all disabled:opacity-50 ${
            enabled ? "bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30" : "bg-[var(--ff-red)]/20 text-[var(--ff-red)] hover:bg-[var(--ff-red)]/30"
          }`}
        >
          {saving ? "Saving…" : enabled ? "Bring Site Back Online" : "Take Site Down"}
        </button>
      </div>

      <div className="bg-[var(--ff-surface)] p-6" data-testid="maintenance-message-card">
        <label className="label-eyebrow block mb-2">Maintenance Message</label>
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value.slice(0, 1000))}
          rows={3}
          maxLength={1000}
          placeholder="We're performing scheduled maintenance and upgrades. The site will be back online shortly — thanks for your patience."
          className="w-full px-4 py-3 text-sm bg-[var(--ff-bg)] border border-[var(--ff-border)] focus:border-[var(--ff-red)] outline-none resize-y"
          data-testid="maintenance-message-input"
        />
        <div className="flex items-center justify-between mt-2">
          <span className="text-[0.65rem] font-mono-ui text-[var(--ff-text-dim)]">{draft.length}/1000</span>
          <button
            onClick={() => onSaveMessage(draft)}
            disabled={saving || !dirty}
            data-testid="maintenance-message-save-btn"
            className="flex items-center gap-2 px-4 py-2 font-mono-ui text-xs uppercase tracking-[0.18em] bg-[var(--ff-red)]/20 text-[var(--ff-red)] hover:bg-[var(--ff-red)]/30 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Save size={13} /> {dirty ? "Save Message" : "Saved"}
          </button>
        </div>
      </div>
    </div>
  );
}

function CodeRedProtocolSection({ enabled, configured, since, clUrl, saving, onToggle }) {
  const active = !enabled; // Code Red is ACTIVE when the bridge is disabled.
  return (
    <div className="space-y-4" data-testid="code-red-section">
      <div>
        <div className="label-eyebrow text-[var(--ff-red)] mb-2">▮ ADMIN / COMMUNITY LOUNGE BRIDGE</div>
        <h2 className="font-display text-3xl mb-2 flex items-center gap-3">
          Code Red Protocol
          {active && (
            <span className="text-[0.6rem] font-mono-ui uppercase tracking-[0.2em] bg-[var(--ff-red)] text-white px-2 py-1 animate-pulse" data-testid="code-red-active-chip">
              ● Active
            </span>
          )}
        </h2>
        <p className="text-[var(--ff-text-dim)] text-sm max-w-2xl">
          Emergency kill-switch for the {clUrl || "Community Lounge"} ⇄ Freshy Fresh bridge. Engaging Code Red makes the
          bridge fully dormant — SSO, role lookups, and cross-app ban sync all stop, and the login button hides.
          The Bridge Audit log stays viewable.
          {since && active && <span className="block mt-2 text-yellow-400">Engaged {new Date(since).toLocaleString()}.</span>}
          {!configured && <span className="block mt-2 text-yellow-400/80">Bridge not configured (no BRIDGE_SHARED_SECRET) — the switch still works but there's nothing to gate yet.</span>}
        </p>
      </div>

      <div className={`p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4 border ${active ? "border-[var(--ff-red)]/50 bg-[var(--ff-red)]/5" : "border-[var(--ff-border)] bg-[var(--ff-surface)]"}`} data-testid="code-red-toggle-card">
        <div className="flex items-start gap-4">
          <AlertOctagon size={26} className={active ? "text-[var(--ff-red)] mt-1" : "text-emerald-400 mt-1"} />
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="font-mono-ui text-sm uppercase tracking-[0.15em]">Bridge</span>
              <span className={`label-eyebrow ${active ? "text-[var(--ff-red)]" : "text-emerald-400"}`} data-testid="code-red-state">
                {active ? "Dormant (Code Red)" : "Live"}
              </span>
            </div>
            <div className="text-xs text-[var(--ff-text-dim)] max-w-xl">
              {active
                ? "All bridge endpoints return 503 and outbound sync is paused. Lift Code Red to resume cross-app SSO + ban sync."
                : "Cross-app SSO and ban/unban sync are active. Engage Code Red to sever the connection instantly."}
            </div>
          </div>
        </div>
        <button
          onClick={() => onToggle(!enabled)}
          disabled={saving}
          data-testid="code-red-toggle-btn"
          className={`px-4 py-2 font-mono-ui text-xs uppercase tracking-[0.18em] transition-all disabled:opacity-50 ${
            active ? "bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30" : "bg-[var(--ff-red)]/20 text-[var(--ff-red)] hover:bg-[var(--ff-red)]/30"
          }`}
        >
          {saving ? "Saving…" : active ? "Lift Code Red" : "Engage Code Red"}
        </button>
      </div>
    </div>
  );
}


function CodeCleanSlateSection({ enabled, since, saving, onToggle }) {
  const active = !enabled; // Clean Slate is ACTIVE when auto-bans are disabled.
  return (
    <div className="space-y-4" data-testid="code-clean-slate-section">
      <div>
        <div className="label-eyebrow text-[var(--ff-red)] mb-2">▮ ADMIN / AUTO-BAN KILL SWITCH</div>
        <h2 className="font-display text-3xl mb-2 flex items-center gap-3">
          Code Clean Slate
          {active && (
            <span className="text-[0.6rem] font-mono-ui uppercase tracking-[0.2em] bg-[var(--ff-red)] text-white px-2 py-1 animate-pulse" data-testid="code-clean-slate-active-chip">
              ● Active
            </span>
          )}
        </h2>
        <p className="text-[var(--ff-text-dim)] text-sm max-w-2xl">
          Pauses <b>automatic</b> blacklist &amp; guild-adjacency bans (the ones that fire on login/sync when a user
          shares a blacklisted server). Everything else keeps running — logins, syncs, blacklist editing, and manual
          moderator bans are unaffected. While active, would-be catches still post to the blacklist alerts channel for transparency.
          {since && active && <span className="block mt-2 text-yellow-400">Engaged {new Date(since).toLocaleString()}.</span>}
        </p>
      </div>

      <div className={`p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4 border ${active ? "border-[var(--ff-red)]/50 bg-[var(--ff-red)]/5" : "border-[var(--ff-border)] bg-[var(--ff-surface)]"}`} data-testid="code-clean-slate-toggle-card">
        <div className="flex items-start gap-4">
          <ShieldOff size={26} className={active ? "text-[var(--ff-red)] mt-1" : "text-emerald-400 mt-1"} />
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="font-mono-ui text-sm uppercase tracking-[0.15em]">Auto-Bans</span>
              <span className={`label-eyebrow ${active ? "text-[var(--ff-red)]" : "text-emerald-400"}`} data-testid="code-clean-slate-state">
                {active ? "Paused (Clean Slate)" : "Active"}
              </span>
            </div>
            <div className="text-xs text-[var(--ff-text-dim)] max-w-xl">
              {active
                ? "No one is auto-banned by the blacklist. Manual bans still work. Turn off Clean Slate to resume automatic enforcement."
                : "Blacklisted servers/users auto-ban matching accounts on login + sync. Engage Clean Slate to pause that instantly."}
            </div>
          </div>
        </div>
        <button
          onClick={() => onToggle(active)}
          disabled={saving}
          data-testid="code-clean-slate-toggle-btn"
          className={`px-4 py-2 font-mono-ui text-xs uppercase tracking-[0.18em] transition-all disabled:opacity-50 ${
            active ? "bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30" : "bg-[var(--ff-red)]/20 text-[var(--ff-red)] hover:bg-[var(--ff-red)]/30"
          }`}
        >
          {saving ? "Saving…" : active ? "Resume Auto-Bans" : "Engage Clean Slate"}
        </button>
      </div>
    </div>
  );
}
